import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart, fmtPrice } from "@/context/CartContext";
import { X, Plus, Minus } from "lucide-react";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, remove, subtotal, makeLineKey } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md p-0 flex flex-col"
        style={{ background: "var(--lf-cream)", border: "none" }}
      >
        <SheetHeader className="px-8 pt-8 pb-4 border-b" style={{ borderColor: "var(--lf-border)" }}>
          <SheetTitle className="font-serif text-3xl italic" style={{ color: "var(--lf-ink)", fontWeight: 400 }}>
            Your Bouquet
          </SheetTitle>
          <p className="lf-eyebrow" style={{ marginTop: 4 }}>
            {items.length} item{items.length === 1 ? "" : "s"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="text-center mt-20" data-testid="cart-empty-state">
              <p className="font-serif text-2xl italic" style={{ color: "var(--lf-ink-soft)" }}>
                Your cart is awaiting petals.
              </p>
              <Link
                to="/shop"
                onClick={() => setIsOpen(false)}
                data-testid="cart-empty-shop-link"
                className="lf-link mt-6 inline-block"
                style={{ color: "var(--lf-gold-deep)" }}
              >
                Browse the shop →
              </Link>
            </div>
          ) : (
            <ul className="space-y-7">
              {items.map((it) => {
                const key = makeLineKey(it);
                return (
                  <li key={key} data-testid={`cart-item-${it.product_id}${it.variant_id ? `-${it.variant_id}` : ""}`} className="flex gap-4">
                    <div className="flex-shrink-0" style={{ width: 88, height: 100, background: "var(--lf-pink-pale)", borderRadius: 12, overflow: "hidden" }}>
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <h4 className="font-serif text-lg leading-tight" style={{ color: "var(--lf-ink)" }}>
                          {it.name}
                        </h4>
                        <button
                          data-testid={`cart-remove-${it.product_id}`}
                          onClick={() => remove(key)}
                          aria-label="Remove"
                          className="text-[var(--lf-ink-soft)] hover:text-[var(--lf-ink)]"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-sm mt-1" style={{ color: "var(--lf-gold-deep)" }}>{fmtPrice(it.price)}</p>
                      {it.notes && (
                        <p className="text-xs mt-2 italic leading-snug" style={{ color: "var(--lf-ink-soft)" }} data-testid={`cart-notes-${it.product_id}`}>
                          {it.notes}
                        </p>
                      )}
                      <div className="mt-3 inline-flex items-center" style={{ border: "1px solid var(--lf-border)", borderRadius: 999, background: "white" }}>
                        <button
                          data-testid={`cart-decrease-${it.product_id}`}
                          onClick={() => updateQty(key, it.quantity - 1)}
                          className="px-2.5 py-1.5"
                          aria-label="Decrease"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-4 text-sm" data-testid={`cart-qty-${it.product_id}`}>
                          {it.quantity}
                        </span>
                        <button
                          data-testid={`cart-increase-${it.product_id}`}
                          onClick={() => updateQty(key, it.quantity + 1)}
                          className="px-2.5 py-1.5"
                          aria-label="Increase"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t px-8 py-7" style={{ borderColor: "var(--lf-border)", background: "white" }}>
            <div className="flex justify-between items-baseline mb-5">
              <span className="lf-eyebrow">Subtotal</span>
              <span className="font-serif text-2xl" style={{ color: "var(--lf-ink)" }} data-testid="cart-subtotal">
                {fmtPrice(subtotal)}
              </span>
            </div>
            <p className="text-xs mb-5" style={{ color: "var(--lf-ink-soft)" }}>
              Delivery fee (if selected) and final totals calculated at checkout.
            </p>
            <Link
              to="/checkout"
              onClick={() => setIsOpen(false)}
              data-testid="cart-checkout-button"
              className="lf-btn lf-btn-gold w-full justify-center"
            >
              Checkout
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
